import React, { useEffect } from 'react'

import { LoadMoreButton, Spinner, Link, ErrorMessage } from '@sketch/components'
import { handleFetchMore } from 'components/InfiniteList'
import Section from 'components/Section'

import { routes } from '@sketch/constants'

import BillingHistoryItem from '../BillingHistoryItem'
import { useGetBillingHistoryQuery } from '@sketch/gql-types'
import { dateFormat } from '@sketch/utils'

import { Paragraph } from './BillingHistoryPanel.styles'
import BillingInfoTable from '../BillingInfoTable'

interface BillingHistoryPanelProps {
  customerId: string
  workspaceId: string
  trialEndDate: string
  customerHasPaymentDetails: boolean
  isWorkspaceOnTrial?: boolean
  cancelAtPeriodEnd?: string
  /** Used to warn the parent component when the data is loaded */
  onLoadComplete: () => void
}

const billingHistoryPath = ['customer', 'billingHistory', 'entries']

export const BillingHistoryPanel: React.FC<BillingHistoryPanelProps> = props => {
  const {
    customerId,
    isWorkspaceOnTrial,
    trialEndDate,
    cancelAtPeriodEnd,
    workspaceId,
    customerHasPaymentDetails,
    onLoadComplete,
  } = props

  const { loading, error, data, fetchMore } = useGetBillingHistoryQuery({
    variables: { customerId },
    notifyOnNetworkStatusChange: true,
    /**
     * "cache-and-network" policy doesn't work very well together with pagination
     * In essence when this query is revisited - "refetch" action is triggered
     * which in return throws away all item previously loaded.
     * However, for such infrequently accessed feature as billing
     * this behaviour seems to be acceptable.
     */
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (!loading) onLoadComplete()
  }, [loading, onLoadComplete])

  let content = null

  if (data) {
    const { meta, entries } = data?.customer?.billingHistory || {}
    const billingItems = entries || []

    if (billingItems.length === 0 && !isWorkspaceOnTrial) {
      content = <p>There is no billing history to display.</p>
    } else {
      content = (
        <>
          <br />
          <BillingInfoTable>
            {billingItems.map((item, index) => (
              <BillingHistoryItem item={item} key={index} />
            ))}
            {entries.length !== meta.totalCount && (
              <LoadMoreButton
                onClick={handleFetchMore(fetchMore, billingHistoryPath, {
                  after: meta.after,
                })}
                loading={loading}
              />
            )}
          </BillingInfoTable>
        </>
      )
    }
  } else if (loading) {
    content = <Spinner.Flex primary />
  } else if (error) {
    content = <ErrorMessage.Generic />
  }

  const text = isWorkspaceOnTrial ? (
    <Paragraph>
      You&apos;re currently on a trial which will end on{' '}
      <strong>{dateFormat(new Date(trialEndDate))}</strong>.{' '}
      {cancelAtPeriodEnd || !customerHasPaymentDetails ? (
        <>
          After that date your subscription will be cancelled and you will not
          be charged.
        </>
      ) : (
        <>
          Once your subscription starts, billing information and invoices will
          appear here.
        </>
      )}
      {!customerHasPaymentDetails && (
        <>
          <br />
          <br />
          <Link
            to={routes.WORKSPACE_SUBSCRIBE.create({
              workspaceId,
            })}
          >
            Set up a subscription
          </Link>{' '}
          to continue using this Workspace.
        </>
      )}
    </Paragraph>
  ) : (
    <Paragraph>
      You can view and download all your previous invoices here. If you’ve just
      made a payment, it may take a few hours for it to&nbsp;appear.
    </Paragraph>
  )

  return (
    <Section title="Billing History" text={text}>
      {/* Billing item lines */}
      {content}
    </Section>
  )
}
